import { useDisclosure } from "@chakra-ui/hooks";
import { Flex } from "@chakra-ui/layout";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import {
  Input,
  FormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { Select as ChakraSelect } from "chakra-react-select";
import GamelightButton from "components/button";
import { PAYMENT_STATUS, PROCESS_STATUS } from "constants/invoice";
import { useTableContext } from "contexts/TableContext";
import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Partner } from "redux/partners/reducer";
import { createNewInvoiceStatus } from "views/admin/invoices/services";
import { getPartners } from "views/admin/partners/services";
import CustomDatePicker from "components/datePicker";

interface IFormInput {
  partnerId: { value: string; label: string };
  amount: string;
  paymentStatus: string;
  range: string;
}

interface NewInvoiceModalProps {}
interface NewInvoiceModalRef {
  openDialog: () => void;
  closeDialog: () => void;
}

const NewInvoiceModal = memo(
  forwardRef(
    (props: NewInvoiceModalProps, ref: React.Ref<NewInvoiceModalRef>) => {
      const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors, isValid },
      } = useForm<IFormInput>({ mode: "onChange" });

      const toast = useToast();
      const { isOpen, onOpen, onClose } = useDisclosure();

      const [partners, setPartners] = useState<Partner[]>([]);
      const [isLoadingPartners, setIsLoadingPartners] = useState(false);
      const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);

      const { refreshData } = useTableContext();

      const fetchPartners = useCallback(async () => {
        try {
          setIsLoadingPartners(true);
          const response = await getPartners({
            page: 1,
            pageSize: 10000,
            filters: {
              hasAtLeastOneCampaign: false,
            },
          });

          setPartners(response.data);
        } catch (error) {
          console.error("Error fetching partners: ", error);
        } finally {
          setIsLoadingPartners(false);
        }
      }, []);

      const openDialog = useCallback(() => {
        if (partners.length === 0) {
          fetchPartners();
        }
        onOpen();
      }, [onOpen, partners, fetchPartners]);

      const closeDialog = useCallback(() => {
        onClose();
        reset();
      }, [onClose, reset]);

      useImperativeHandle(ref, () => ({
        openDialog,
        closeDialog,
      }));

      const onSubmit: SubmitHandler<IFormInput> = useCallback(
        async (data) => {
          try {
            const partnerId = Number(data.partnerId);
            const partner = partners.find((itm) => itm.partnerId === partnerId);

            setIsCreatingInvoice(true);
            const response = await createNewInvoiceStatus({
              partnerId,
              companyName: partner.companyName,
              amount: Number(data.amount.replace(",", ".")),
              paymentStatus: data.paymentStatus,
              processStatus: "SENT",
              range: data.range,
            });

            toast({
              title: "Invoice created",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
            refreshData();
            closeDialog();
          } catch (error: any) {
            console.error("Error creating invoice: ", error);
            toast({
              title: "Error creating invoice",
              description: error,
              status: "error",
              duration: 9000,
              isClosable: true,
            });
          } finally {
            setIsCreatingInvoice(false);
          }
        },
        [toast, partners, refreshData, closeDialog]
      );

      return (
        <Modal
          isOpen={isOpen}
          onClose={closeDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>New Invoice</ModalHeader>
            <ModalCloseButton />
            <form onSubmit={handleSubmit(onSubmit)}>
              <ModalBody>
                <Flex gap={4} direction="column">
                  <FormControl isInvalid={!!errors.partnerId}>
                    <FormLabel>Company Name</FormLabel>
                    <Select
                      {...register("partnerId", {
                        required: "Company Name is required",
                      })}
                    >
                      <option value="">Select Company Name</option>
                      {partners.map((partner) => (
                        <option
                          key={partner.partnerId}
                          value={partner.partnerId}
                        >
                          {partner.companyName}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.partnerId && errors.partnerId.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.amount}>
                    <FormLabel>Amount</FormLabel>
                    <Input
                      {...register("amount", {
                        required: "Amount is required",
                        pattern: {
                          value: /^\d+([.,]\d{1,2})?$/,
                          message:
                            "Enter a valid amount (e.g., 100, 55.05 or 55,05)",
                        },
                        validate: (value) =>
                          Number(value.replace(",", ".")) > 0 ||
                          "Amount must be positive",
                      })}
                      placeholder="Enter amount (e.g., 100, 55.05 or 55,05)"
                    />
                    <FormErrorMessage>
                      {errors.amount && errors.amount.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.paymentStatus}>
                    <FormLabel>Payment Status</FormLabel>
                    <Select
                      {...register("paymentStatus", {
                        required: "Payment Status is required",
                      })}
                    >
                      <option value="">Select Payment Status</option>
                      {Object.entries(PAYMENT_STATUS).map(([key, value]) => (
                        <option key={key} value={value.name}>
                          {value.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.paymentStatus && errors.paymentStatus.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.range}>
                    <FormLabel>Range</FormLabel>
                    <Controller
                      name="range"
                      control={control}
                      rules={{ required: "Range is required" }}
                      render={({ field }) => (
                        <CustomDatePicker
                          onChange={field.onChange}
                          value={field.value}
                          pickerType="month"
                          placeholder="Select month and year"
                        />
                      )}
                    />
                    <FormErrorMessage>
                      {errors.range && errors.range.message}
                    </FormErrorMessage>
                  </FormControl>
                </Flex>
              </ModalBody>
              <ModalFooter gap={2}>
                <GamelightButton variant="ghost" onClick={closeDialog}>
                  Cancel
                </GamelightButton>
                <GamelightButton
                  type="submit"
                  colorScheme="brand"
                  isLoading={isCreatingInvoice}
                  isDisabled={!isValid}
                >
                  Create Invoice Tracker
                </GamelightButton>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>
      );
    }
  )
);

export default NewInvoiceModal;

export type { NewInvoiceModalProps, NewInvoiceModalRef };
